import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { InfoscreenFooterComponent } from '../../infoscreen-dumb/infoscreen-footer/infoscreen-footer.component';
import { InfoscreenHeaderComponent } from '../../infoscreen-dumb/infoscreen-header/infoscreen-header.component';
import { AdBandComponent } from '../ad-band/ad-band.component';
import { CalendarViewComponent } from '../../school-calendar/calendar-view.component';
import { PagePieComponent } from '../../school-calendar/page-pie.component';
import { InfoscreenModuleService } from '../infoscreen-module.service';
import {
  InfoscreenFeed,
  InfoscreenFeedAd,
  InfoscreenNewsItem,
  OpeningHoursToday,
  SCREEN_SIZES,
} from '../infoscreen-module.types';
import { ListViewComponent } from '../list-view/list-view.component';
import { MessageBarComponent } from '../message-bar/message-bar.component';
import { RoomViewComponent } from '../room-view/room-view.component';
import { WelcomePaneComponent } from '../welcome-pane/welcome-pane.component';
import { NewsTickerComponent } from '../news/news-ticker.component';
import { degrees } from '../weather/weather-card.component';
import { WeatherIconComponent } from '../weather/weather-icon.component';
import { WeatherPageComponent } from '../weather/weather-page.component';
import { calendarDecorClass, hasOwnDecorations, skyBody, themeClass } from './day-phase';
import { DEMO_PRESETS, DEMO_SECONDS, demoElapsed, demoPreset, withPreset } from './demo-presets';
import { moonPath, moonPhase, moonUp } from './moon-phase';
import { Holiday, holiday, PARTICLE_SLOTS, particlesRise, yearScene } from './year-scene';
import { isAsleep } from './sleep';

/** The holiday each holiday theme is for. */
const THEME_HOLIDAY: Partial<Record<string, Holiday>> = {
  Halloween: 'halloween',
  Christmas: 'christmas',
  Easter: 'easter',
  NewYear: 'newyear',
  Eid: 'eid',
  LunarNewYear: 'lunarnewyear',
  Diwali: 'diwali',
  Fastelavn: 'fastelavn',
  SanktHans: 'sankthans',
};

/** What the screen wishes on each holiday. */
const GREETINGS: Record<Holiday, string> = {
  fastelavn: 'God fastelavn',
  easter: 'God påske',
  sankthans: 'God sankthans',
  halloween: 'Glædelig halloween',
  christmas: 'Glædelig jul',
  newyear: 'Godt nytår',
  eid: 'Eid Mubarak',
  lunarnewyear: 'Godt kinesisk nytår',
  diwali: 'Glædelig Diwali',
};

/** The wind speed (m/s) at which the clouds drift at their own pace. */
const WIND_REFERENCE = 5;

/** How fast things fall, in m/s: rain, and the light things (leaves, petals, snow). */
const RAIN_FALL_SPEED = 7;
const LIGHT_FALL_SPEED = 2;

/** A wind from the east half of the compass (between north and south, through east). */
function windFromEast(degrees: number): boolean {
  const d = ((degrees % 360) + 360) % 360;
  return d > 10 && d < 170;
}

/** The forecast page: shown for 20 seconds every 2 minutes. */
const WEATHER_CYCLE_SECONDS = 120;
const WEATHER_PAGE_SECONDS = 20;

/** The weather and the news, put among the ads by the screen itself. */
const WEATHER_ITEM: InfoscreenFeedAd = { id: -1, type: 'Weather', imageUrl: null, headline: null, body: null, backgroundColor: null, textColor: null };
const NEWS_ITEM: InfoscreenFeedAd = { id: -2, type: 'News', imageUrl: null, headline: null, body: null, backgroundColor: null, textColor: null };

/** A headline as a card among the ads: the headline, and its source in place of the body. */
function newsCard(item: InfoscreenNewsItem, index: number): InfoscreenFeedAd {
  return { id: -100 - index, type: 'News', imageUrl: null, headline: item.title, body: item.source, backgroundColor: null, textColor: null };
}

/** Seconds between two feed reads — the same cycle as the old screen. */
const REFRESH_SECONDS = 60;

/**
 * One configured screen of the infoscreen module. Reads its whole feed once a minute. The header's
 * progress bar is hidden here: a page turn is shown by the pie beside "1/3" instead.
 *
 * A failed read keeps the last feed on the wall — except 403/404, which mean the screen is gone:
 * then the feed is dropped (stale ads and "closed today" messages must not stay up) and `gone`
 * fires, so a screen on the old address can fall back to the old view. The error text keeps
 * retrying, so a screen recovers by itself when the module is switched on again.
 */
@Component({
  selector: 'app-screen-host',
  imports: [
    InfoscreenHeaderComponent,
    InfoscreenFooterComponent,
    MessageBarComponent,
    ListViewComponent,
    CalendarViewComponent,
    AdBandComponent,
    RoomViewComponent,
    WelcomePaneComponent,
    NewsTickerComponent,
    WeatherIconComponent,
    WeatherPageComponent,
    PagePieComponent,
  ],
  templateUrl: './screen-host.component.html',
  styleUrls: [
    './screen-host.component.scss',
    './screen-themes.scss',
    './screen-day-cycle.scss',
    './screen-sky.scss',
    './screen-year.scss',
    './screen-drizzle.scss',
    './screen-sky-weather.scss',
    './screen-sky-light.scss',
    './screen-sky-holiday.scss',
    './screen-easter.scss',
    './screen-fastelavn-sankthans.scss',
    './screen-newyear.scss',
    './screen-eid.scss',
    './screen-asian.scss',
    './screen-greeting.scss',
    './screen-weather-page.scss',
    './screen-sky-moon.scss',
    './screen-decor-top.scss',
  ],
  host: { '[class]': 'hostClass()' },
})
export class ScreenHostComponent implements OnInit, OnDestroy {
  private readonly moduleService = inject(InfoscreenModuleService);

  readonly screenId = input.required<number>();
  /** In a rotating demo: how many times this screen has had its turn, so a demo screen shows the next preset. */
  readonly demoTurn = input<number | null>(null);
  /** The screen no longer exists for this tenant (403/404): deleted, switched off, or module off. */
  readonly gone = output<void>();

  /** The feed as read. */
  private readonly loaded = signal<InfoscreenFeed | null>(null);
  /** On a demo screen, the set-up it shows now — the same object until the next one, so nothing redraws in between. */
  readonly demo = computed(() => demoPreset(this.loaded(), this.now(), this.demoTurn()));
  /**
   * The demo's badge: the preset's name and number, and the seconds it has been up when it comes on
   * (worked out again only then), so the clock beside it fills until the next one.
   */
  readonly demoBadge = computed(() => {
    const demo = this.demo();
    if (!demo) return null;
    return { label: demo.label, index: DEMO_PRESETS.indexOf(demo), elapsed: untracked(() => demoElapsed(this.now())) };
  });
  readonly demoCount = DEMO_PRESETS.length;
  readonly demoSeconds = DEMO_SECONDS;
  /** The feed the screen shows: as read, or with the demo's theme and layout. */
  readonly feed = computed(() => {
    const feed = this.loaded();
    const demo = this.demo();
    return feed && demo ? withPreset(feed, demo) : feed;
  });
  readonly error = signal<string | null>(null);
  readonly now = signal(new Date());
  readonly loading = signal(false);

  /**
   * The footer line: the anlæg and, when opening hours are set up, today's. Built here and handed
   * to the old footer as its text, so the old screen's footer stays untouched.
   */
  readonly footerText = computed(() => {
    const feed = this.feed();
    if (!feed) return '';
    const hours = openingHoursText(feed.openingHoursToday);
    return hours
      ? `${feed.locationName ?? ''} · ${hours}`
      : (feed.locationName ?? '');
  });

  /**
   * Dark outside opening hours: from 15 minutes after closing until 15 minutes before opening, and
   * all day on a closed day. Only when opening hours are set up for the anlæg. The feed is still
   * read every minute while dark, so the screen wakes by itself.
   */
  readonly asleep = computed(() => {
    const feed = this.feed();
    return !!feed && isAsleep(feed.openingHoursToday, this.minute());
  });

  /**
   * `theme-classic`, `theme-halloween` …, or for a day-cycle theme the part of the day, e.g.
   * `theme-cycle-dark-evening` — it follows the minute, so the screen changes by itself. The
   * variables are in the stylesheets.
   */
  readonly themeClass = computed(() =>
    themeClass(this.feed()?.colorTheme ?? 'Classic', this.skyClock(), this.hiddenHolidays()),
  );

  /** The holidays this screen leaves out, as the calendar names them (`SanktHans` → `sankthans`). */
  private readonly hiddenHolidays = computed(
    () => (this.feed()?.hiddenHolidays ?? []).map((h) => h.toLowerCase() as Holiday),
  );

  /**
   * With the sky switched on (any theme; by default the sun-following ones): the sun's (or moon's)
   * place in its arc over the top of the screen. Null without a sky.
   */
  readonly sky = computed(() => {
    const feed = this.feed();
    if (!feed) return null;
    if (!(feed.showSun ?? feed.colorTheme.startsWith('DayCycle'))) return null;
    const clock = this.skyClock();
    const body = skyBody(clock);
    const own = { across: body.across, lift: body.lift };
    const sun = body.body === 'sun' ? own : null;
    // With the weather shown, the moon follows the forecast: up only between moonrise and moonset,
    // across its own arc in that time — but never beside the sun: by day the sky has the sun alone.
    // Beyond the forecast (a preview of another day) the screen's own moon, across the whole night.
    const weather = this.shownWeather();
    const across = weather?.moonRises
      ? moonUp(
          clock,
          weather.moonRises.map((t) => new Date(t)),
          (weather.moonSets ?? []).map((t) => new Date(t)),
        )
      : undefined;
    if (across === undefined) return { sun, moon: sun ? null : own };
    return { sun, moon: sun || across === null ? null : { across, lift: 4 * across * (1 - across) } };
  });

  /**
   * With the weather shown, the moon has its real shape: the lit part for the moment (the preview's
   * date and time, else now), or nothing lit at new moon. Without, null — the usual crescent.
   */
  readonly moonShape = computed(() => {
    if (!this.feed()?.showWeather) return null;
    // The forecast's phase for today; the screen's own reckoning for a previewed day.
    const phase = this.shownWeather()?.moonPhase;
    const real = phase != null && this.previewDate() === null;
    return { lit: moonPath(real ? phase / 360 : moonPhase(this.skyClock())) };
  });

  /**
   * The holiday greeting by the date ("Glædelig jul", "Eid Mubarak" …): on a holiday the screen is
   * decorated for — its theme (the Christmas theme at Christmas), or the calendar's decorations on
   * "Følger året" and the themes that borrow them. Null on other days, and without decorations.
   */
  readonly greeting = computed(() => {
    const feed = this.feed();
    if (!feed || feed.showDecorations === false) return null;
    const today = holiday(this.skyClock(), this.hiddenHolidays());
    if (!today) return null;
    const decorated =
      THEME_HOLIDAY[feed.colorTheme] === today || this.hostClass().split(' ').includes(`year-decor-${today}`);
    return decorated ? { text: GREETINGS[today], holiday: today } : null;
  });

  /** The year theme's particles: the CSS classes for the kind, or null without that theme. */
  readonly drizzle = computed(() => {
    // On "Følger året", and on any theme without decorations of its own when they are switched on.
    const theme = this.feed()?.colorTheme;
    if (!theme || this.feed()?.showDecorations === false) return null;
    if (theme !== 'DayCycleYear' && hasOwnDecorations(theme)) return null;
    const particles = yearScene(this.skyClock(), this.hiddenHolidays()).particles;
    return `drizzle ${particles}${particlesRise(particles) ? ' rise' : ''}`;
  });

  readonly particleSlots = PARTICLE_SLOTS;

  /**
   * A time of day (minutes after midnight) and a date set by the admin's preview, or null. Only
   * the theme and the sky follow them — the bookings, the clock and the sleep keep the real time.
   */
  private readonly previewMinutes = signal<number | null>(null);
  private readonly previewDate = signal<{
    year: number;
    month: number;
    day: number;
  } | null>(null);

  /** The time the theme and the sky are drawn for: the preview's date and time, else now. */
  private readonly skyClock = computed(() => {
    const minutes = this.previewMinutes();
    const date = this.previewDate();
    if (minutes === null && date === null) return this.minute();
    const at = new Date(this.minute());
    if (date) at.setFullYear(date.year, date.month - 1, date.day);
    if (minutes !== null) at.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return at;
  });

  /** The decorations only at the top, with the bottom band as a divider under the header. */
  readonly decorAtTop = computed(() => !!this.feed()?.decorAtTop && this.feed()?.showDecorations !== false);

  /**
   * How far down the top reaches, in px of the laid-out screen: to the bottom of the divider under
   * the header. The sprinkle falls and the passers-by walk within it. Null without a divider (a
   * door screen) — then the stylesheet's share of the height.
   */
  readonly decorTopHeight = signal<number | null>(null);
  private dividerObserver?: ResizeObserver;

  /** The divider under the header, measured whenever it (or what is above it) changes size. */
  @ViewChild('decorDivider') set decorDivider(ref: ElementRef<HTMLElement> | undefined) {
    this.dividerObserver?.disconnect();
    if (!ref) {
      this.decorTopHeight.set(null);
      return;
    }
    const el = ref.nativeElement;
    const measure = () => this.decorTopHeight.set(el.offsetTop + el.offsetHeight);
    this.dividerObserver = new ResizeObserver(measure);
    // The header above it (and the stage around it) change its place without changing its size.
    this.dividerObserver.observe(el);
    if (el.previousElementSibling) this.dividerObserver.observe(el.previousElementSibling);
    if (el.parentElement) this.dividerObserver.observe(el.parentElement);
    measure();
  }

  /** While previewing, the sun jumps to the chosen time instead of gliding there over a minute. */
  readonly hostClass = computed(() => {
    const classes = [this.themeClass()];
    const theme = this.feed()?.colorTheme ?? 'Classic';
    // A theme without decorations of its own gets the calendar's, when they are switched on.
    const borrowed = this.feed()?.showDecorations ? calendarDecorClass(theme, this.skyClock(), this.hiddenHolidays()) : null;
    if (borrowed) classes.push(borrowed);
    if (this.previewMinutes() !== null || this.previewDate() !== null) classes.push('previewing');
    if (this.feed()?.showDecorations === false) classes.push('no-decor');
    else if (this.decorAtTop()) classes.push('decor-at-top');
    if (this.weatherPageShown() && this.feed()?.weatherMode === 'Page' && this.shownWeather()) classes.push('weather-page-on');
    return classes.join(' ');
  });

  /**
   * The admin's preview sets the time of day and the date with
   * `{ type: 'abook-infoscreen-preview-time', minutes, date: 'yyyy-mm-dd' }` (null for now). Only
   * inside a frame — a screen on the wall is never in one — and it changes nothing but how this
   * window looks, so the sender's origin does not matter.
   */
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (window.parent === window) return;
    const data = event.data as {
      type?: unknown;
      minutes?: unknown;
      date?: unknown;
    } | null;
    if (data?.type !== 'abook-infoscreen-preview-time') return;
    const minutes = data.minutes;
    this.previewMinutes.set(
      typeof minutes === 'number' && minutes >= 0 && minutes < 24 * 60
        ? Math.round(minutes)
        : null,
    );
    const date =
      typeof data.date === 'string'
        ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(data.date)
        : null;
    this.previewDate.set(
      date ? { year: +date[1], month: +date[2], day: +date[3] } : null,
    );
  }

  readonly degrees = degrees;

  /**
   * The weather the screen shows (temperature, forecast). The feed may carry the weather for the sky
   * and the wind alone; then this is null.
   */
  readonly shownWeather = computed(() => {
    const feed = this.feed();
    return feed?.showWeather ? feed.weather : null;
  });

  /**
   * The ad band's items: the forecast first when it is shown "as an ad", then the ads — and, when
   * the news goes "with the ads", a card per headline among them.
   */
  readonly bandAds = computed<InfoscreenFeedAd[]>(() => {
    const feed = this.feed();
    if (!feed) return [];
    const items = this.shownWeather() && feed.weatherMode === 'Ad' ? [WEATHER_ITEM, ...feed.ads] : [...feed.ads];
    if (feed.newsMode !== 'WithAds') return items;
    return [...items, ...feed.news.map((item, i) => newsCard(item, i))];
  });

  /** The news as a running line (not when it goes with the ads). */
  readonly ticker = computed(() => {
    const feed = this.feed();
    return !!feed && feed.news.length > 0 && feed.newsMode === 'Ticker';
  });

  /** The welcome view's (or pane's) items: the forecast and the ads, and the headlines as one card. */
  readonly showcase = computed<InfoscreenFeedAd[]>(() => {
    const items = this.bandAds().filter((item) => item.type !== 'News');
    return this.feed()?.news.length ? [...items, NEWS_ITEM] : items;
  });

  /**
   * The forecast "as a page": shown for the last WEATHER_PAGE_SECONDS of every WEATHER_CYCLE_SECONDS,
   * by the clock — so screens side by side change together.
   */
  readonly weatherPageShown = computed(
    () => Math.floor(this.now().getTime() / 1000) % WEATHER_CYCLE_SECONDS >= WEATHER_CYCLE_SECONDS - WEATHER_PAGE_SECONDS,
  );

  /**
   * The sky follows the weather (sun-following themes): `weather-rain`, `weather-cloudy` … and
   * `from-east` when the wind blows from the east, so the clouds drift right to left.
   */
  readonly skyWeather = computed(() => {
    const weather = this.feed()?.weather;
    if (!weather) return 'sky';
    return `sky weather-${weather.condition.toLowerCase()}${windFromEast(weather.windFromDirection) ? ' from-east' : ''}`;
  });

  /**
   * How the wind carries what falls: rain sideways by its share of the wind against a rain drop's
   * fall of some 7 m/s (at most 45°), and the lighter leaves, petals, snow and sparks a good deal
   * more. Only the wind's east–west part moves them across the screen; positive is to the right.
   */
  readonly windDrift = computed(() => {
    const weather = this.feed()?.weather;
    if (!weather) return { rain: 0, rainSlant: 0, light: 0 };
    const across = -weather.windSpeed * Math.sin((weather.windFromDirection * Math.PI) / 180);
    const rain = Math.max(-1, Math.min(1, across / RAIN_FALL_SPEED));
    return {
      rain,
      // A streak leans with its fall: top back, bottom ahead.
      rainSlant: (-Math.atan(rain) * 180) / Math.PI,
      light: Math.max(-1.5, Math.min(1.5, across / LIGHT_FALL_SPEED)),
    };
  });

  /**
   * How long the clouds take, as a share of their calm-breeze time: 1 at 5 m/s, faster with more
   * wind (a quarter of the time in a storm), slower with less (four times in still air).
   */
  readonly windFactor = computed(() => {
    const speed = this.feed()?.weather?.windSpeed;
    return speed === undefined ? 1 : Math.min(4, Math.max(0.25, WIND_REFERENCE / Math.max(speed, 0.5)));
  });

  /** Where the rain (or snow) in the sky falls from, and how far into its fall each starts. */
  readonly precipSlots = Array.from({ length: 14 }, (_, i) => ({ x: (i * 37) % 100, delay: ((i * 7) % 10) / 10 }));

  /** A welcome pane beside the times (the list and the calendars only). */
  readonly split = computed(() => {
    const feed = this.feed();
    return (
      !!feed &&
      feed.splitSide !== 'None' &&
      (feed.viewType === 'List' || feed.viewType === 'WeekCalendar' || feed.viewType === 'DayCalendar')
    );
  });

  /** The fixed size the screen is drawn at, or null to follow the window. */
  readonly stage = computed(() => {
    const size = this.feed()?.screenSize;
    return size && size !== 'Auto' ? SCREEN_SIZES[size] : null;
  });

  private readonly windowSize = signal({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  /** Scales the fixed stage to fit the window and centres it; the spare side becomes bars. */
  readonly stageTransform = computed(() => {
    const stage = this.stage();
    if (!stage) return null;

    const { width, height } = this.windowSize();
    const scale = Math.min(width / stage.width, height / stage.height);
    const x = (width - stage.width * scale) / 2;
    const y = (height - stage.height * scale) / 2;
    return `translate(${x}px, ${y}px) scale(${scale})`;
  });

  /** Zoom as a factor; 0.8 lays the screen out 1/0.8 as large and shrinks it back. */
  private readonly zoom = computed(() =>
    Math.min(1.5, Math.max(0.5, (this.feed()?.zoom ?? 100) / 100)),
  );

  /** The screen's own size: the stage, or the window. Every size in the module is measured from it. */
  readonly base = computed(() => this.stage() ?? this.windowSize());

  /** The size the views lay themselves out in: the screen, divided by the zoom. */
  readonly layout = computed(() => {
    const base = this.base();
    const zoom = this.zoom();
    return { width: base.width / zoom, height: base.height / zoom };
  });

  readonly zoomTransform = computed(() =>
    this.zoom() === 1 ? null : `scale(${this.zoom()})`,
  );

  @HostListener('window:resize')
  onResize() {
    this.windowSize.set({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }

  /** Reported by the list view: true while it scrolls vertically. */
  readonly listScrolling = signal(false);

  /**
   * While the list scrolls vertically the ad band shows a set at a time instead of scrolling
   * sideways, so the screen never moves two ways at once. A paged list does not move, so the ads
   * scroll.
   */
  readonly listScrolls = computed(
    () => this.feed()?.viewType === 'List' && this.listScrolling(),
  );

  /**
   * `now` to the minute, for the views. The clock in the header needs seconds; the layouts only
   * change on the minute, and rebuilding the timetable every second costs a wall screen for nothing.
   */
  readonly minute = computed(
    () => new Date(Math.floor(this.now().getTime() / 60_000) * 60_000),
    {
      equal: (a, b) => a.getTime() === b.getTime(),
    },
  );

  private tickId?: ReturnType<typeof setInterval>;
  private elapsedTicks = 0;

  async ngOnInit() {
    await this.refresh();

    // One 1 s tick drives the clock and the refresh. Once a second: each tick is a change-detection
    // pass on weak hardware.
    this.tickId = setInterval(() => {
      this.elapsedTicks++;
      this.now.set(new Date());

      if (this.elapsedTicks >= REFRESH_SECONDS) {
        this.elapsedTicks = 0;
        void this.refresh();
      }
    }, 1000);
  }

  ngOnDestroy() {
    clearInterval(this.tickId);
    this.dividerObserver?.disconnect();
  }

  private async refresh() {
    this.loading.set(true);
    try {
      const feed = await firstValueFrom(
        this.moduleService.getFeed(this.screenId()),
      );
      // A different view is a different component: the old one's report no longer applies.
      if (feed.viewType !== this.feed()?.viewType)
        this.listScrolling.set(false);
      this.loaded.set(feed);
      this.error.set(null);
    } catch (e) {
      if (isGone(e)) {
        this.loaded.set(null);
        this.listScrolling.set(false);
        this.error.set(describe(e));
        this.gone.emit();
      } else if (!this.feed()) {
        this.error.set(describe(e));
      }
    } finally {
      this.now.set(new Date());
      this.loading.set(false);
    }
  }
}

/** "Åbent i dag 07:00–22:00", "Lukket i dag (Juledag)", or null when nothing is stated. */
export function openingHoursText(day: OpeningHoursToday | null): string | null {
  if (!day) return null;
  if (!day.intervals.length)
    return day.closedLabel
      ? `Lukket i dag (${day.closedLabel})`
      : 'Lukket i dag';

  // The API writes midnight as 24:00 for a close; on a sign "00:00" reads better.
  const range = (i: { opens: string; closes: string }) =>
    `${i.opens}–${i.closes === '24:00' ? '00:00' : i.closes}`;
  return `Åbent i dag ${day.intervals.map(range).join(', ')}`;
}

function isGone(e: unknown): boolean {
  return (
    e instanceof HttpErrorResponse && (e.status === 403 || e.status === 404)
  );
}

function describe(e: unknown): string {
  if (e instanceof HttpErrorResponse) {
    if (e.status === 404) return 'Skærmen findes ikke eller er slået fra.';
    if (e.status === 403) return 'Infoskærmmodulet er ikke aktivt.';
  }
  return 'Skærmen kan ikke hente data lige nu. Prøver igen om lidt.';
}
